import graphql__b275898fa8476c1b889f from "./__generated__/AuthenticationSignIn_authenticationConfig.graphql.js";import { useState, useMemo, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { useSelector } from "react-redux";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import withFormStore, { useForm } from "../Form/withFormStore";
import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR } from "../Alerts/Alert";
import { useFormatMessage } from "../Intl/Context";
import AuthenticationCaptcha from "./AuthenticationCaptcha";
import EmailField from "../Form/Field/EmailField";
import PasswordField from "../Form/Field/PasswordField";
import CheckBoxField from "../Form/Field/CheckBoxField";
import Button from "../Buttons/Button";
import { withConfirmationForm } from "./withConfirmationForm";
import { withPasswordForm } from "./withPasswordForm";
import { withAccountLocked } from "./withAccountLocked";
import { useCaptcha } from "../Form/Field/CaptchaField";
import { useUnlockForm } from "./useUnlockForm";
import { useStoreLocationForReload } from "../Utils/routing";
import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";
import AuthenticationTwoFactorManager from "./AuthenticationTwoFactorManager";

import { signIn } from "../../mutations/SignInMutation";

const AuthenticationSignIn = props => {
  const authenticationConfig = useFragment(
    AuthenticationSignIn_authenticationConfig,
    props.authenticationConfig
  );
  const { allowUserAccountCreation } = authenticationConfig;

  const {
    onSignIn,
    onAlertMessageClicked,
    unconfirmedErrorMessage,
    accountLockedErrorMessage,
    forgotPasswordErrorMessage,
    showPasswordForm,
    showConfirmationForm
  } = props;

  const { showUnlockForm } = useUnlockForm();
  const storeLocationForReload = useStoreLocationForReload();

  const lastNonModalLocation = useSelector(
    ({ location }) => location?.lastNonModalLocation
  );

  const { formState, validateForm } = useForm();
  const [, resetCaptcha] = useCaptcha();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState(null);

  const t = useFormatMessage();

  const labels = useMemo(
    () => ({
      title: t(intlMessages.title, {
        b: message => `<b>${message}</b>`,
        br: "<br>"
      }),
      requiredFieldsHint: t(formPageIntlMessages.requiredFieldsHint),
      email: t(intlMessages.email),
      password: t(intlMessages.password),
      submit: t(intlMessages.submit),
      rememberMe: t(intlMessages.rememberMe),
      resetPassword: t(intlMessages.resetPassword),
      confirmAccount: t(intlMessages.confirmAccount),
      unlockAccount: t(intlMessages.unlockAccount)
    }),
    [t]
  );

  const formClassName = classNames({
    "authentication-fieldset": true,
    "authentication-sign-in": true,
    box: true,
    "box-margin-top-mobile": true,
    "padding-md": true,
    loading: isSubmitting
  });

  const currentEmail = formState?.fields?.email?.value;

  // Declare the `handleSignInResponse` before it's used and assigned below
  let handleSignInResponse;

  const handleSignedIn = useCallback(async () => {
    if (onSignIn) {
      onSignIn();
    } else {
      // Reload page on sign in to avoid stale data to hang around after
      // the user is authenticated.
      await storeLocationForReload(lastNonModalLocation);

      window.location.href = window.location;
    }
  }, [lastNonModalLocation, onSignIn, storeLocationForReload]);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      // Do not submit form if it is already submitting
      if (isSubmitting) return;

      if (validateForm()) {
        setIsSubmitting(true);

        signIn(formState, handleSignInResponse);
      }
    },
    [formState, handleSignInResponse, isSubmitting, validateForm]
  );

  const { showAlert } = useAlert();

  handleSignInResponse = useCallback(
    response => {
      setIsSubmitting(false);

      if (response.signIn.success) {
        handleSignedIn();
      } else if (response.signIn.twoFactorStep) {
        // Credentials were valid but the user must complete an SMS step before being
        // signed in; swap the sign-in form for the matching two-factor step.
        setTwoFactorStep(response.signIn.twoFactorStep);
      } else {
        let message;

        if (response.signIn.invalidCaptcha) {
          message = response.signIn.notice;
        } else if (response.signIn.user?.confirmationNeeded) {
          message = unconfirmedErrorMessage;
        } else if (response.signIn.user?.accountLocked) {
          message = accountLockedErrorMessage;
        } else {
          message = forgotPasswordErrorMessage;
        }

        resetCaptcha();

        showAlert({
          message,
          type: ALERT_TYPE_ERROR,
          onClick: onAlertMessageClicked
        });
      }
    },
    [
      accountLockedErrorMessage,
      forgotPasswordErrorMessage,
      handleSignedIn,
      onAlertMessageClicked,
      resetCaptcha,
      showAlert,
      unconfirmedErrorMessage
    ]
  );

  if (twoFactorStep) {
    return (
      <AuthenticationTwoFactorManager
        onSignIn={handleSignedIn}
        twoFactorStep={twoFactorStep}
      />
    );
  }

  return (
    <>
      <form className={formClassName} onSubmit={handleSubmit} data-sign-in-form>
        <fieldset>
          <legend
            className="text-subtitle-page"
            dangerouslySetInnerHTML={{ __html: labels.title }}
          />

          <p
            className="form-hint-required-fields margin-bottom-sm text-small"
            dangerouslySetInnerHTML={{ __html: labels.requiredFieldsHint }}
          />

          <EmailField
            fieldName="email"
            required
            label={labels.email}
            autoComplete="email"
          />
          <PasswordField
            fieldName="password"
            required
            label={labels.password}
            autoComplete="current-password"
          />

          <div className="form-group-addon form-group-remember-me">
            <CheckBoxField
              fieldName="rememberMe"
              schema={{ type: "boolean" }}
              label={labels.rememberMe}
              checked={false}
            />
          </div>

          <AuthenticationCaptcha authenticationConfig={authenticationConfig} />
        </fieldset>

        <Button
          type="submit"
          className="btn btn-primary btn-block-md"
          onClick={handleSubmit}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          label={labels.submit}
          iconClassName="icon-arrow-right"
          iconPosition="after"
          data-submit-button-sign-in
        />

        <div className="margin-top-sm">
          <Button
            className="btn-link text-legend d-block"
            onClick={() => showPasswordForm(currentEmail)}
            type="button"
            label={labels.resetPassword}
          />

          {allowUserAccountCreation && (
            <Button
              className="btn-link text-legend d-block margin-top-xxs"
              onClick={() => showConfirmationForm(currentEmail)}
              type="button"
              label={labels.confirmAccount}
            />
          )}

          <Button
            className="btn-link text-legend d-block margin-top-xxs"
            onClick={() => showUnlockForm(currentEmail)}
            type="button"
            label={labels.unlockAccount}
            data-send-unlock-instructions-link
          />
        </div>
      </form>
    </>
  );
};

const AuthenticationSignIn_authenticationConfig = graphql__b275898fa8476c1b889f;

export default withAccountLocked(
  withConfirmationForm(withPasswordForm(withFormStore(AuthenticationSignIn)))
);

const intlMessages = defineMessages({
  title: {
    id: "authentication.signIn.title",
    defaultMessage: "Déjà un compte ?{br}<b>Connectez vous !</b>"
  },
  email: {
    id: "authentication.signIn.labels.email",
    defaultMessage: "E-mail"
  },
  password: {
    id: "authentication.signIn.labels.password",
    defaultMessage: "Mot de passe"
  },
  submit: {
    id: "authentication.signIn.submit",
    defaultMessage: "Me connecter"
  },
  rememberMe: {
    id: "authentication.signIn.rememberMe",
    defaultMessage: "Se souvenir de moi"
  },
  resetPassword: {
    id: "authentication.signIn.resetPassword",
    defaultMessage: "J'ai oublié mon mot de passe"
  },
  confirmAccount: {
    id: "authentication.signIn.confirmAccount",
    defaultMessage: "Je n'ai pas reçu mon mail de confirmation"
  },
  unlockAccount: {
    id: "authentication.signIn.unlockAccount",
    defaultMessage: "Débloquer mon compte"
  }
});
